/* ============================================
   NAVIGATION COMPONENT
   The Creative Orchestra - GEMINI Framework
   Creative persistent navigation with dropdowns
   ============================================ */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  ChevronDown, 
  BookOpen, 
  Sparkles,
  User,
  Home,
  GraduationCap,
} from 'lucide-react';
import { NAV_LINKS, WORKS_ORDER } from '@utils/constants';
import { works } from '@data';
import { useTheme } from '@context/ThemeContext';

/**
 * Navigation Component
 * Creative, persistent navigation with:
 * - Glassmorphism effect
 * - Animated dropdown for works
 * - Mobile responsive menu
 * - Route-aware active states
 * - Theme-aware styling
 */
const Navigation = ({ currentWork = null }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isWorksDropdownOpen, setIsWorksDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTheme } = useTheme();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsWorksDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsWorksDropdownOpen(false);
  }, [location.pathname]);

  // Check if link is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Get accent color for current theme
  const accentColor = activeTheme?.colors?.accent || '#818CF8';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${isScrolled 
            ? 'bg-black/60 backdrop-blur-xl border-b border-white/10' 
            : 'bg-transparent'
          }
        `}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/"
              className="flex items-center gap-3 group"
            >
              <motion.div
                whileHover={{ rotate: 180, scale: 1.1 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${accentColor}40, ${accentColor}20)`,
                  boxShadow: `0 0 20px ${accentColor}30`,
                }}
              >
                <Sparkles className="w-5 h-5 text-white" />
              </motion.div>
              <div className="hidden sm:block">
                <span className="text-lg font-display font-semibold text-white">
                  GEMINI
                </span>
                <span className="text-xs text-gray-400 block -mt-1">
                  The Creative Orchestra
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {/* Home */}
              <NavLink 
                to="/" 
                isActive={isActive('/')} 
                icon={<Home className="w-4 h-4" />}
              >
                Home
              </NavLink>

              {/* Novel */}
              <NavLink 
                to="/novel" 
                isActive={isActive('/novel')}
                icon={<BookOpen className="w-4 h-4" />}
              >
                Novel
              </NavLink>

              {/* Thesis */}
              <NavLink 
                to="/thesis" 
                isActive={isActive('/thesis')}
                icon={<GraduationCap className="w-4 h-4" />}
              >
                Thesis
              </NavLink>

              {/* Works Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsWorksDropdownOpen(!isWorksDropdownOpen)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg
                    text-sm font-medium transition-all duration-200
                    ${isActive('/work') 
                      ? 'text-white bg-white/10' 
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  <Sparkles className="w-4 h-4" />
                  Works
                  <ChevronDown 
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isWorksDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isWorksDropdownOpen && (
                    <WorksDropdown 
                      onClose={() => setIsWorksDropdownOpen(false)}
                      currentPath={location.pathname}
                    />
                  )}
                </AnimatePresence>
              </div>

              {/* About */}
              <NavLink 
                to="/about" 
                isActive={isActive('/about')} 
                icon={<User className="w-4 h-4" />}
              >
                About
              </NavLink>

              {/* Publications */}
              <NavLink 
                to="/publications" 
                isActive={isActive('/publications')} 
                icon={<BookOpen className="w-4 h-4" />}
              >
                Publications
              </NavLink>

              {/* Acknowledgements */}
              <NavLink 
                to="/acknowledgements" 
                isActive={isActive('/acknowledgements')} 
                icon={<Sparkles className="w-4 h-4" />}
              >
                Acknowledgements
              </NavLink>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu 
            onClose={() => setIsMobileMenuOpen(false)}
            currentPath={location.pathname}
          />
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * Navigation Link Component
 */
const NavLink = ({ to, children, isActive, icon }) => {
  return (
    <Link
      to={to}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-lg
        text-sm font-medium transition-all duration-200
        ${isActive 
          ? 'text-white bg-white/10' 
          : 'text-gray-300 hover:text-white hover:bg-white/5'
        }
      `}
    >
      {icon}
      {children}
    </Link>
  );
};

/**
 * Works Dropdown Component
 */
const WorksDropdown = ({ onClose, currentPath }) => {
  const { getOrbGradient } = useTheme();

  const geminiWork = works['gemini'];

  // Group works by stage
  const stages = [
    { name: 'Generation', letter: 'G', works: ['genie', 'midas'] },
    { name: 'Evaluation', letter: 'E', works: ['magics'] },
    { name: 'Management', letter: 'M', works: ['dimes', 'aegis', 'traces'] },
    { name: 'INspiration', letter: 'IN', works: ['euphoria', 'retina', 'fenso'] },
  ];

  const isGeminiActive = currentPath === '/work/gemini';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="absolute top-full right-0 mt-2 w-80 p-4 rounded-2xl
        bg-black/80 backdrop-blur-xl border border-white/10
        shadow-2xl"
    >
      {/* GEMINI Framework link at top */}
      {geminiWork && (
        <div className="mb-4 pb-3 border-b border-white/10">
          <Link
            to="/work/gemini"
            onClick={onClose}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg
              transition-all duration-200
              ${isGeminiActive 
                ? 'bg-white/10 text-white' 
                : 'hover:bg-white/5 text-gray-300 hover:text-white'
              }
            `}
          >
            <div 
              className="w-8 h-8 rounded-full flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${geminiWork.colorTheme.accent}40, ${geminiWork.colorTheme.accent}20)`,
                boxShadow: `0 0 10px ${geminiWork.colorTheme.accent}30`,
              }}
            />
            <div className="flex-grow min-w-0">
              <div className="font-semibold text-sm">{geminiWork.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {geminiWork.tagline}
              </div>
            </div>
          </Link>
        </div>
      )}

      {stages.map((stage, stageIndex) => (
        <div key={stage.name} className={stageIndex > 0 ? 'mt-4' : ''}>
          <div className="flex items-center gap-2 mb-2 px-2">
            <span 
              className="text-xs font-bold px-2 py-0.5 rounded bg-white/10 text-gray-400"
            >
              {stage.letter}
            </span>
            <span className="text-xs text-gray-500 uppercase tracking-wider">
              {stage.name}
            </span>
          </div>
          <div className="space-y-1">
            {stage.works.map((workId) => {
              const work = works[workId];
              if (!work) return null;
              
              const isActive = currentPath === `/work/${workId}`;
              
              return (
                <Link
                  key={workId}
                  to={`/work/${workId}`}
                  onClick={onClose}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-lg
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-white/10 text-white' 
                      : 'hover:bg-white/5 text-gray-300 hover:text-white'
                    }
                  `}
                >
                  <div 
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    style={{
                      background: work.colorTheme 
                        ? `linear-gradient(135deg, ${work.colorTheme.accent}40, ${work.colorTheme.accent}20)`
                        : 'rgba(255,255,255,0.1)',
                      boxShadow: work.colorTheme 
                        ? `0 0 10px ${work.colorTheme.accent}30`
                        : 'none',
                    }}
                  />
                  <div className="flex-grow min-w-0">
                    <div className="font-medium text-sm">{work.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {work.tagline}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </motion.div>
  );
};

/**
 * Mobile Menu Component
 */
const MobileMenu = ({ onClose, currentPath }) => {
  const [expandedStage, setExpandedStage] = useState(null);

  const geminiWork = works['gemini'];

  const stages = [
    { name: 'Generation', letter: 'G', works: ['genie', 'midas'] },
    { name: 'Evaluation', letter: 'E', works: ['magics'] },
    { name: 'Management', letter: 'M', works: ['dimes', 'aegis', 'traces'] },
    { name: 'INspiration', letter: 'IN', works: ['euphoria', 'retina', 'fenso'] },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 md:hidden"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />

      {/* Menu Content */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw]
          bg-black/95 backdrop-blur-xl border-l border-white/10
          overflow-y-auto"
      >
        <div className="p-6 pt-24">
          {/* Main Links */}
          <div className="space-y-2 mb-8">
            <MobileNavLink 
              to="/" 
              icon={<Home className="w-5 h-5" />}
              isActive={currentPath === '/'}
              onClick={onClose}
            >
              Home
            </MobileNavLink>
            
            <MobileNavLink 
              to="/novel" 
              icon={<BookOpen className="w-5 h-5" />}
              isActive={currentPath.startsWith('/novel')}
              onClick={onClose}
            >
              Novel
            </MobileNavLink>

            <MobileNavLink 
              to="/thesis" 
              icon={<GraduationCap className="w-5 h-5" />}
              isActive={currentPath === '/thesis'}
              onClick={onClose}
            >
              Thesis
            </MobileNavLink>
            
            <MobileNavLink 
              to="/about" 
              icon={<User className="w-5 h-5" />}
              isActive={currentPath === '/about'}
              onClick={onClose}
            >
              About
            </MobileNavLink>

            <MobileNavLink 
              to="/publications" 
              icon={<BookOpen className="w-5 h-5" />}
              isActive={currentPath === '/publications'}
              onClick={onClose}
            >
              Publications
            </MobileNavLink>

            <MobileNavLink 
              to="/acknowledgements" 
              icon={<Sparkles className="w-5 h-5" />}
              isActive={currentPath === '/acknowledgements'}
              onClick={onClose}
            >
              Acknowledgements
            </MobileNavLink>
          </div>

          {/* Works */}
          <div className="border-t border-white/10 pt-6">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-4 px-2">
              Works
            </h3>

            {/* GEMINI Framework link at top */}
            {geminiWork && (
              <div className="mb-4 pb-3 border-b border-white/10">
                <Link
                  to="/work/gemini"
                  onClick={onClose}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-colors
                    ${currentPath === '/work/gemini'
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  <div 
                    className="w-6 h-6 rounded-full"
                    style={{
                      background: `linear-gradient(135deg, ${geminiWork.colorTheme.accent}40, ${geminiWork.colorTheme.accent}20)`,
                    }}
                  />
                  <span className="text-sm font-semibold">{geminiWork.name}</span>
                </Link>
              </div>
            )}
            
            {stages.map((stage) => (
              <div key={stage.name} className="mb-4">
                <button
                  onClick={() => setExpandedStage(
                    expandedStage === stage.name ? null : stage.name
                  )}
                  className="w-full flex items-center justify-between px-2 py-2 
                    text-gray-400 hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-white/10">
                      {stage.letter}
                    </span>
                    <span className="text-sm">{stage.name}</span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 transition-transform ${
                      expandedStage === stage.name ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                <AnimatePresence>
                  {expandedStage === stage.name && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 py-2 space-y-1">
                        {stage.works.map((workId) => {
                          const work = works[workId];
                          if (!work) return null;
                          
                          return (
                            <Link
                              key={workId}
                              to={`/work/${workId}`}
                              onClick={onClose}
                              className={`
                                flex items-center gap-3 px-3 py-2 rounded-lg
                                transition-colors
                                ${currentPath === `/work/${workId}`
                                  ? 'bg-white/10 text-white'
                                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }
                              `}
                            >
                              <div 
                                className="w-6 h-6 rounded-full"
                                style={{
                                  background: work.colorTheme 
                                    ? `linear-gradient(135deg, ${work.colorTheme.accent}40, ${work.colorTheme.accent}20)`
                                    : 'rgba(255,255,255,0.1)',
                                }}
                              />
                              <span className="text-sm">{work.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * Mobile Navigation Link
 */
const MobileNavLink = ({ to, children, icon, isActive, onClick }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl
        transition-colors
        ${isActive 
          ? 'bg-white/10 text-white' 
          : 'text-gray-400 hover:text-white hover:bg-white/5'
        }
      `}
    >
      {icon}
      <span className="font-medium">{children}</span>
    </Link>
  );
};

export default Navigation;
